/**
 * Autocomplete tag-input for assigning groups to a user with add/remove chip UI.
 *
 * @module components/settings/GroupTagInput
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React from 'react';
import PropTypes from 'prop-types';
import useAutocomplete from '@material-ui/lab/useAutocomplete';
import NoSsr from '@material-ui/core/NoSsr';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { addGroupToUser } from '../../redux/admin/action';

/**
 * Styled component for wrapping input elements.
 *
 * This component provides a styled container with a border, padding, and
 * flexible layout capabilities to encapsulate input elements. It supports
 * hover and focus styles for enhanced user interactivity.
 *
 * - Default state: Displays with a solid border and white background.
 * - Hover state: Changes the border color for visual feedback.
 * - Focused state: Highlights the wrapper with a custom border color
 *   and a subtle shadow.
 *
 * Additional Features:
 * - The child `input` element adopts styles for consistent sizing, spacing,
 *   and responsiveness within the wrapper, ensuring a cohesive visual design.
 */
const InputWrapper = styled('div')`
    border: 1px solid #d9d9d9;
    background-color: #fff;
    border-radius: 4px;
    padding: 6px;
    display: flex;
    flex-wrap: wrap;
    &:hover {
        border-color: #40a9ff;
    }

    &.focused {
        border-color: #40a9ff;
        box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }

    & input {
        font-size: 14px;
        height: 30px;
        box-sizing: border-box;
        padding: 4px 6px;
        width: 0;
        min-width: 30px;
        flex-grow: 1;
        border: 0;
        margin: 0;
        outline: 0;
    }
`;

/**
 * A styled component that represents a tag element with customizable label and delete functionality.
 *
 * This component is designed to display a tag with a text label and a delete (close) icon. The tag
 * has a visually styled appearance, supports keyboard focus, and provides overflow handling for
 * long text labels.
 *
 * The delete action is triggered by clicking on the close icon.
 *
 * Component overrides and additional props can be passed for further customization.
 *
 * Props:
 * @param {string} label - The text label to display within the tag.
 * @param {function} onDelete - A callback function to handle the delete action when the close icon is clicked.
 * @param {Object} props - Additional props to customize the rendered tag further.
 *
 * Styling:
 * - Displays flex layout with centered alignment.
 * - Features padding, background color, bordered edges, and rounded corners.
 * - Implements smooth focus and hover states for enhanced interactivity.
 * - Text within the tag is truncated with ellipsis when it overflows the available space.
 * - The close icon supports hover states and background styling for better visual indication.
 */
const Tag = styled(({ label, onDelete, ...props }) => (
    <div {...props}>
        <span>{label}</span>
        <CloseIcon onClick={onDelete} />
    </div>
))`
    display: flex;
    align-items: center;
    height: 24px;
    margin: 2px;
    line-height: 22px;
    background-color: #4B6AA3;
    border: 1px solid #e8e8e8;
    color: #fff;
    border-radius: 5px;
    box-sizing: content-box;
    padding: 1px 3px 0px 10px;
    font-size: 12px;
    outline: 0;
    overflow: hidden;

    &:focus {
        border-color: #40a9ff;
        background-color: #e6f7ff;
    }

    & span {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & svg {
        font-size: 8px;
        cursor: pointer;
        padding: 4px;
        background: #8197BF;
        margin: 6px;
        border-radius: 2px;
    }
`;

/**
 * A styled list component designed to act as a container for selectable list items.
 *
 * The Listbox component is visually styled and includes item-specific styles for different states.
 * It supports custom styling for focused items, selected items, and general item layout.
 *
 * Key Characteristics:
 * - Fixed width of 300px and maximum height of 250px with scrollable overflow.
 * - Absolute positioning to overlay or attach directly below a related UI element.
 * - Includes default padding for items while maintaining zero padding and margin for the container itself.
 * - Rounded corners and shadow effects for a polished dropdown appearance.
 * - Custom styling for list items (`<li>`), including specific states like `aria-selected` and `data-focus`.
 *
 * States:
 * - `aria-selected='true'`: Applies a highlighted background color, bold font styling, and a visible icon color for selected states.
 * - `data-focus='true'`: Applies a hover-like background color, changes the cursor to pointer, and adjusts icon color for focused states.
 *
 * Usage:
 * Designed for use cases like dropdowns, autocomplete suggestions, or selectable lists in user interfaces.
 */
const Listbox = styled('ul')`
    width: 300px;
    margin: 2px 0 0;
    padding: 0;
    position: absolute;
    list-style: none;
    background-color: #fff;
    overflow: auto;
    max-height: 250px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 1;

    & li {
        padding: 5px 12px;
        display: flex;

        & span {
            flex-grow: 1;
        }

        & svg {
            color: transparent;
        }
    }

    & li[aria-selected='true'] {
        background-color: #fafafa;
        font-weight: 600;

        & svg {
            color: #1890ff;
        }
    }

    & li[data-focus='true'] {
        background-color: #e6f7ff;
        cursor: pointer;

        & svg {
            color: #000;
        }
    }
`;

/**
 * Component representing a tag-based input field with user selection and group management functionality.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.groupUsers - Object containing a list of users associated with a group.
 * @param {Object[]} props.groupUsers.users - List of user objects associated with the group.
 * @param {Object[]} props.employees - List of all available employees.
 * @param {Object} props.prop - Additional property data, including group-related information.
 * @param {function} props.addGroupToUser - Callback function to add or remove a group association for a user.
 *
 * @returns {JSX.Element} The rendered TagInput component.
 */
const TagInput = ({
    groupUsers, employees, prop, addGroupToUser,
}) => {
    const filteredArray = groupUsers && groupUsers.users
        ? employees.filter((user) => groupUsers.users.includes(user._id)) : [];
    filteredArray.map((user) => {
        user.delete = true;
    });
    const {
        getRootProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value,
        focused,
        setAnchorEl,
    } = useAutocomplete({
        id: 'customized-hook-demo',
        defaultValue: filteredArray,
        multiple: true,
        options: employees,
        getOptionLabel: (option) => `${option.first_name} ${option.last_name}`,
    });

    /**
     * Handles the click event for updating group association with a user.
     * Toggles the `delete` property of the provided option and sends the updated data
     * to the `addGroupToUser` function.
     *
     * @param {Object} option - The option object representing a group's association.
     * @param {string} option._id - The unique identifier of the option.
     * @param {boolean} option.delete - Determines whether the group is marked for deletion.
     * @property {Object} prop - Contains context-specific properties.
     * @property {string} prop._id - The unique identifier of the current group context.
     * @throws {Error} If `prop` or its required properties are undefined.
     */
    const click = (option) => {
        if (option.delete) {
            option.delete = false;
            const data = {
                _id: option._id,
                group_id: prop._id,
                delete: true,
            };
            addGroupToUser(data);
        } else {
            option.delete = true;
            const data = {
                _id: option._id,
                group_id: prop._id,
            };
            addGroupToUser(data);
        }
    };

    return (
        <NoSsr>
            <div>
                <div {...getRootProps()}>
                    <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''} name="members">
                        {value.map((option, index) => (
                            <Tag
                                label={`${option.first_name} ${option.last_name}`}
                                {...getTagProps({ index })}
                                onClickCapture={() => click(option)}
                                key={option._id}
                            />
                        ))}

                        <input {...getInputProps()} />
                    </InputWrapper>
                </div>
                {groupedOptions.length > 0 ? (
                    <Listbox {...getListboxProps()}>
                        {groupedOptions.map((option, index) => (
                            <li {...getOptionProps({ option, index })}
                                onClickCapture={() => click(option)}
                                key={option.id}
                            >
                                <span>{`${option.first_name} ${option.last_name}`}</span>
                                <CheckIcon fontSize="small" />
                            </li>
                        ))}
                    </Listbox>
                ) : null}
            </div>
        </NoSsr>
    );
};

TagInput.propTypes = {
    groupUsers: PropTypes.object,
    employees: PropTypes.array,
    prop: PropTypes.object,
    addGroupToUser: PropTypes.func,
};

const mapStateToProps = (state) => ({
    group: state.admin.group,
    employees: state.admin.employees ? state.admin.employees : [],
});

const mapDispatchToProps = { addGroupToUser };

export default connect(mapStateToProps, mapDispatchToProps)(TagInput);
